import type { ReactElement } from 'react';
import { BookOpen, ClipboardList, EyeOff, FileText } from 'lucide-react';
import { formatDate } from '@xdocs/i18n';
import type { SearchResultRow } from '@xdocs/schemas';
import { Badge, Button, Card, Chip, Tooltip, cn, useI18n } from '@xdocs/ui';
import type { BadgeTone } from '@xdocs/ui';
import { resolveCategoryLabel } from './categoryLabel.js';
import { HighlightedSnippet } from './HighlightedSnippet.js';
import { useSearchT } from './useSearchT.js';
import styles from './SearchResultCard.module.css';

const KIND_ICON = {
  book: BookOpen,
  article: FileText,
  report: ClipboardList,
} as const;

const DIFFICULTY_TONE: Record<SearchResultRow['difficultyLevel'], BadgeTone> = {
  basic: 'neutral',
  intermediate: 'info',
  advanced: 'warning',
  mastery: 'pro',
};

export interface SearchResultCardProps {
  result: SearchResultRow;
  onOpen: (result: SearchResultRow) => void;
  onHide: (result: SearchResultRow) => void;
  isHiding?: boolean;
}

/** One `search.query` result (F8), with a per-item F30 "never show again"
 * action and F31 category chips. */
export function SearchResultCard({ result, onOpen, onHide, isHiding = false }: SearchResultCardProps): ReactElement {
  const { locale, t: tGlobal } = useI18n();
  const { t } = useSearchT();
  const Icon = KIND_ICON[result.kind];

  return (
    <Card>
      <div className={cn(styles['card'])}>
        <span className={cn(styles['iconTile'])} aria-hidden="true">
          <Icon size={18} />
        </span>
        <div className={cn(styles['body'])}>
          <div className={cn(styles['headerRow'])}>
            <button type="button" className={cn(styles['title'])} onClick={() => onOpen(result)}>
              {result.title}
            </button>
            <Tooltip content={t('search:result.hideTooltip')}>
              <Button
                size="icon"
                variant="ghost"
                aria-label={t('search:result.hideAction')}
                state={isHiding ? 'loading' : 'idle'}
                onClick={() => onHide(result)}
              >
                <EyeOff size={16} aria-hidden="true" />
              </Button>
            </Tooltip>
          </div>

          <div className={cn(styles['metaRow'])}>
            <Badge tone={DIFFICULTY_TONE[result.difficultyLevel]}>{t(`search:filters.difficulty.${result.difficultyLevel}`)}</Badge>
            <span>{result.language}</span>
            {result.publishedAt && <span>{t('search:result.publishedOn', { date: formatDate(locale, result.publishedAt, { dateStyle: 'medium' }) })}</span>}
          </div>

          <p className={cn(styles['snippet'])}>
            <HighlightedSnippet html={result.snippetHtml} />
          </p>

          {result.categories.length > 0 && (
            <div className={cn(styles['categories'])}>
              {result.categories.map((category) => (
                <Chip key={category.id}>{resolveCategoryLabel(tGlobal, category)}</Chip>
              ))}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
